import React, { useState } from 'react';

import { isMobileOperatingSystem as getIsMobileOperatingSystem } from '@repo/detection';
import { RevampedCheckoutFlowMobile } from '@repo/editor/src/components/UpgradeFlowModal/RevampedCheckoutFlowMobile';
import { useRevampedCheckoutFlowVariant } from '@repo/editor/src/global/growthbookUtils';
import {
  teamsStoreSelector,
  useTeamsStore,
} from '@repo/editor/src/stores/teamsStore';
import { CreateTeamFlowState } from '@repo/editor/src/stores/teamsStore/types';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { ModalProps, Modal } from '@repo/ui/deprecated/Modal';

import ChooseTeamPlanFlowContent, {
  INITIAL_DIMENSIONS,
} from 'components/ChooseTeamPlanFlow/ChooseTeamPlanFlowContent';
import { MODAL_DIMENSIONS } from 'components/InviteTeammember/data';
import InviteTeammemberModalContent from 'components/InviteTeammember/InviteTeammemberModalContent';
import teamsApi from 'global/teamsApi';

import CreateTeamModalContent from './CreateTeamModalContent/CreateTeamModalContent';
import { CreateTeamFlowProps } from './types';

/**
 * component to manage the team creation flow
 */
const CreateTeamFlow: React.FC<CreateTeamFlowProps> = ({
  isOpen,
  onClose,
  onResetData,
}) => {
  const state = useTeamsStore(teamsStoreSelector.createTeamFlowState);
  const setState = useTeamsStore(teamsStoreSelector.setCreateTeamFlowState);
  const [choosePlanDimensions, setShowPlanDimensions] =
    useState<Pick<ModalProps, 'width' | 'height'>>(INITIAL_DIMENSIONS);

  const team = useUserStore(userStoreSelector.user)?.team;
  const swapMainUser = useUserStore(userStoreSelector.swapMainUser);

  const handleOnTeamCreated = (): void => {
    onResetData?.();
    setState(CreateTeamFlowState.ChoosePlan);
  };

  const modalDimension =
    state === CreateTeamFlowState.CreateTeam
      ? { width: '520px' }
      : state === CreateTeamFlowState.ChoosePlan
        ? choosePlanDimensions
        : MODAL_DIMENSIONS;

  const handleDeleteTeam = async (): Promise<void> => {
    if (!team?.slug) {
      throw new Error('Team is not defined');
    }
    await teamsApi.deleteTeam(team.slug);
    await swapMainUser();
    onClose();
  };

  const handleClose = async (): Promise<void> => {
    if (state === CreateTeamFlowState.CreateTeam || !team) {
      onClose();
      return;
    }
    if (!team.active) {
      await handleDeleteTeam();
      return;
    }
    if (state === CreateTeamFlowState.ChoosePlan) {
      setState(CreateTeamFlowState.InviteMembers);
      return;
    }
    onClose();

    // TODO: This has a flash of the step before closing -- we need to do this "on close end"
    setState(CreateTeamFlowState.CreateTeam);
  };

  // Revamped checkout flow
  const showRevampedCheckoutFlow = useRevampedCheckoutFlowVariant() !== 'off';
  const shouldShowRevampedMobileCheckoutFlow =
    showRevampedCheckoutFlow && getIsMobileOperatingSystem();

  const CreateTeamFlowModal = shouldShowRevampedMobileCheckoutFlow
    ? RevampedCheckoutFlowMobile
    : Modal;

  const updatedDimensions =
    showRevampedCheckoutFlow && state === CreateTeamFlowState.ChoosePlan
      ? {
          height: '720px',
          width: modalDimension.width,
        }
      : modalDimension;

  return (
    <CreateTeamFlowModal
      isOpen={isOpen}
      onClose={handleClose}
      {...updatedDimensions}
    >
      {state === CreateTeamFlowState.CreateTeam && (
        <CreateTeamModalContent onSuccess={handleOnTeamCreated} />
      )}
      {state === CreateTeamFlowState.ChoosePlan && (
        <ChooseTeamPlanFlowContent
          showFreePlan
          canChooseFreePlan={!!team?.active}
          isOpen={isOpen}
          onClose={handleClose}
          onDimensionsChange={setShowPlanDimensions}
          onSuccess={(): void => {
            setState(CreateTeamFlowState.InviteMembers);
          }}
          onSuccessLabel="Invite Members"
          onSuccessMessage="Your payment was successful. Invite your team members to start working on projects together."
        />
      )}
      {!!team && state === CreateTeamFlowState.InviteMembers && (
        <InviteTeammemberModalContent team={team} />
      )}
    </CreateTeamFlowModal>
  );
};

export default withEventScope(CreateTeamFlow, { feature: 'createTeamFlow' });
